import { store, getElement } from '@wordpress/interactivity';
import '../front.scss';

// Counter for generating unique animation IDs
let animationIdCounter = 0;

// Global tracking of observed elements to prevent duplicate observers
const observedElements = new Set();

// Global IntersectionObserver (created lazily)
let globalObserver = null;

// Global MutationObserver (created lazily)
let globalMutationObserver = null;

// Helper function to generate and assign unique animation IDs
const assignAnimateId = (element) => {
  if (!element || element.hasAttribute('data-wp-animate-id')) {
    return; // Already assigned
  }

  const classes = element.className || '';
  let animationType = 'animation';

  // Map all supported animation types from animation-[name] classes
  const animationTypeMap = {
    'animation-bounce': 'bounce',
    'animation-fade': 'fade',
    'animation-fade-in-up': 'fade-in-up',
    'animation-fade-in-up-big': 'fade-in-up-big',
    'animation-fade-in-down': 'fade-in-down',
    'animation-fade-in-down-big': 'fade-in-down-big',
    'animation-fade-in-left': 'fade-in-left',
    'animation-fade-in-right': 'fade-in-right',
    'animation-heartbeat': 'heartbeat',
    'animation-pulse': 'pulse',
  };

  // Find matching animation type
  for (const [classPattern, typeName] of Object.entries(animationTypeMap)) {
    if (classes.includes(classPattern)) {
      animationType = typeName;
      break;
    }
  }

  // Generate unique ID: data-wp-animate-id="[type]-[counter]"
  const uniqueId = `${animationType}-${++animationIdCounter}`;
  element.setAttribute('data-wp-animate-id', uniqueId);
  console.debug(`Animate ID assigned: ${uniqueId}`, element);
};

// Helper to safely parse JSON attributes
const parseAnimationContext = (element) => {
  try {
    // Check for HOC animation config first, then fall back to data-wp-context
    let ctxAttr = element.getAttribute('data-amp-animate');
    if (!ctxAttr) {
      ctxAttr = element.getAttribute('data-wp-context');
    }
    return ctxAttr ? JSON.parse(ctxAttr) : {};
  } catch (error) {
    console.error('Failed to parse animation context:', error, element);
    return {};
  }
};

// Setup repeat animation logic for a block
const setupRepeatAnimation = (block) => {
  const ctx = parseAnimationContext(block);

  const animationDuration = parseFloat(ctx.animationDuration || 1);
  const animationDelay = parseFloat(ctx.animationDelay || 0);
  const animationRepeatDelay = parseFloat(ctx.animationRepeatDelay || 0);
  const shouldRepeat = ctx.animationRepeat === true || ctx.animationRepeat === 'true';

  const totalDuration = animationDuration + animationDelay;

  const runCycle = () => {
    // Only run if block is still in view
    if (!block.__inViewTriggered) return;

    // Start animation
    block.classList.add('is-animating');

    // Schedule removal after animationDuration + animationDelay
    setTimeout(() => {
      if (!block.__inViewTriggered) return;
      block.classList.remove('is-animating');

      if (shouldRepeat) {
        const repeatDelay = animationRepeatDelay > 0 ? animationRepeatDelay * 1000 : 0;
        setTimeout(runCycle, repeatDelay);
      }
    }, totalDuration * 1000);
  };

  // Start the first cycle
  runCycle();
};

// Mark element as in view and trigger animation
const markInView = (block) => {
  const ctx = parseAnimationContext(block);
  const reverse = ctx.animationReverse === true || ctx.animationReverse === 'true';

  if (!block.__inViewTriggered) {
    block.__inViewTriggered = true;
    block.classList.add('is-in-view', 'is-animating');
    setupRepeatAnimation(block);
  }

  if (globalObserver && !reverse) {
    globalObserver.unobserve(block);
    observedElements.delete(block);
  }
};

// Create the global IntersectionObserver
const createGlobalObserver = () => {
  if (globalObserver) return globalObserver;

  globalObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const block = entry.target;
      const ctx = parseAnimationContext(block);
      const threshold = !isNaN(parseFloat(ctx.animationThreshold)) ? parseFloat(ctx.animationThreshold) : 0.1;
      const reverse = ctx.animationReverse === true || ctx.animationReverse === 'true';

      if (entry.intersectionRatio >= threshold && (threshold > 0 || entry.intersectionRatio > 0)) {
        markInView(block);
      } else if (reverse && block.__inViewTriggered) {
        block.classList.remove('is-in-view', 'is-animating');
        block.__inViewTriggered = false;
      }
    });
  }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });

  return globalObserver;
};

// Check if element is currently visible in viewport
const isElementInView = (element) => {
  const rect = element.getBoundingClientRect();
  const ctx = parseAnimationContext(element);
  const threshold = !isNaN(parseFloat(ctx.animationThreshold)) ? parseFloat(ctx.animationThreshold) : 0.1;

  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const elementHeight = rect.height || 1;
  const visibleHeight = Math.max(0, Math.min(windowHeight, rect.bottom) - Math.max(0, rect.top));
  const visibleRatio = visibleHeight / elementHeight;

  return visibleRatio >= threshold;
};

// Main function to observe animation blocks
const observeAnimationBlocks = () => {
  const observer = createGlobalObserver();

  // Select all elements that should be animated (both Container and HOC blocks)
  const selector = '.has-in-view-animation, [data-amp-animate]';
  const elements = document.querySelectorAll(selector);

  elements.forEach((element) => {
    // Skip if already being observed
    if (observedElements.has(element)) return;

    // Assign unique ID
    assignAnimateId(element);

    // Add to tracking set
    observedElements.add(element);

    // Start observing
    observer.observe(element);

    // Check if element is already in view (for elements visible on page load)
    if (isElementInView(element)) {
      markInView(element);
    }
  });
};

// Initialize mutation observer to watch for dynamically added elements
const initMutationObserver = () => {
  if (globalMutationObserver) return;

  globalMutationObserver = new MutationObserver(() => {
    // Re-run observation setup for any new elements
    observeAnimationBlocks();
  });

  globalMutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// Interactivity API store for Container blocks
store('amplify_storytelling_animateIn', {
  state: {},
  actions: {},
  callbacks: {
    init: () => {
      const el = getElement()?.ref;
      console.debug('Animate init callback triggered', { el });
      if (!el) return;

      // Assign unique ID to this element
      assignAnimateId(el);

      // Use unified initialization
      observeAnimationBlocks();

      // Ensure mutation observer is running
      initMutationObserver();
    },
  },
});

// Initialize on DOMContentLoaded for HOC blocks and any missed elements
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    observeAnimationBlocks();
    initMutationObserver();
  });
} else {
  // DOM already loaded, initialize immediately
  observeAnimationBlocks();
  initMutationObserver();
}
